import { useCallback, useContext, type MutableRefObject } from 'react';
import type { AdyenCheckoutError } from '@adyen/adyen-web';

import { useCheckoutData } from '@/api/useCheckoutData';
import { APPCHARGE_ERROR_TYPE_TO_KEY } from '@/constants/error';
import { ErrorLogs } from '@/constants/logs';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { mapAdyenSdkError } from '@/utils/adyen';
import { classifyApplePayCancel, isApplePayDomainFailure } from '@/utils/applePayCancelDiagnostics';
import { markApplePayDomainFailure } from '@/utils/applePayTabFallback';
import { logError } from '@/utils/logger';
import { notifyPaymentIntentFailed } from '@/utils/notifier';

export type AdyenExpressPaymentMethod = PaymentMethodEnum.APPLE_PAY | PaymentMethodEnum.GOOGLE_PAY;

interface UseAdyenExpressErrorParams {
  paymentMethod: AdyenExpressPaymentMethod;
  // Refs are owned by the consumer hook because other branches (onClick
  // guard, onSubmit) read/write them too. We only flip them off here.
  isPaymentInProgress: MutableRefObject<boolean>;
  isPaySessionStartingRef: MutableRefObject<boolean>;
  // Optional wallet-specific diagnostics merged into the failure log's data —
  // read lazily at error time so the caller can report live state (e.g. the
  // Google Pay sheet phase / ms-since-click for CALLBACK_TIMED_OUT triage).
  getDiagnostics?: () => Record<string, string | number | boolean>;
}

interface UseAdyenExpressErrorReturn {
  handleError: (err?: AdyenCheckoutError) => void;
  handleCancel: () => void;
}

// ACDEV-24170: shared error path for the Adyen Apple Pay and Google Pay
// wallet hooks (`useAdyenApplePay`, `useAdyenGooglePay`). The Adyen SDK
// contract is identical for both wallets, so the only thing that varies
// between them is `paymentMethod`. The mapper (`mapAdyenSdkError`) is the
// source of truth — `null` means "treat as user-initiated cancellation"
// (silent dismiss, no log/UI/FE event); anything else gets logged to
// Coralogix, surfaced to the player via the existing `appchargeErrors.*`
// i18n keys, and emitted as PAYMENT_INTENT_FAILED.
export const useAdyenExpressError = ({
  paymentMethod,
  isPaymentInProgress,
  isPaySessionStartingRef,
  getDiagnostics,
}: UseAdyenExpressErrorParams): UseAdyenExpressErrorReturn => {
  const {
    actions: { setError },
  } = useContext(CheckoutContext);
  const checkoutData = useCheckoutData({ enableFetch: false });
  const { t } = useTranslate();

  const handleCancel = useCallback(() => {
    isPaymentInProgress.current = false;
    isPaySessionStartingRef.current = false;
  }, []);

  const handleError = useCallback(
    (err?: AdyenCheckoutError) => {
      const mappedError = mapAdyenSdkError(err?.name);
      const applePayDiagnostics = paymentMethod === PaymentMethodEnum.APPLE_PAY ? classifyApplePayCancel() : undefined;

      // ACDEV-25945: explicit express-wallet failure log, emitted for EVERY
      // Adyen Apple Pay / Google Pay error — including the ones `mapAdyenSdkError`
      // treats as a silent cancel (the early return below). The merchant-
      // validation rejection that breaks Apple Pay in Safari Private Browsing
      // surfaces as an Adyen CANCEL, indistinguishable from a user closing the
      // sheet. `paymentMethod` distinguishes the wallet; for Apple Pay,
      // `classifyApplePayCancel` further disambiguates using the merchant session
      // captured at completeMerchantValidation:
      //   - domain_mismatch: sent domainName != real top-level origin
      //   - merchant_validation_rejected: CANCEL immediately after a valid
      //     validation (PassKit rejected: cert / merchant-id / association)
      //   - user_cancelled: CANCEL after a human-length delay
      // order_id rides the global RUM labels.
      logError(ErrorLogs.expressPaymentFailed, err, {
        paymentMethod,
        provider: ProviderEnum.ADYEN,
        adyenErrorName: err?.name,
        appchargeErrorType: mappedError ?? 'CANCEL',
        ...applePayDiagnostics,
        ...(getDiagnostics ? getDiagnostics() : {}),
      });

      // Apple refused the merchant session for the embedding page: the real
      // button is dead for the rest of this session on EVERY iOS version, so
      // latch the new-tab fallback in (a top-level tab on our own verified
      // domain still pays). No-op outside the framed-phone gate.
      if (isApplePayDomainFailure(applePayDiagnostics?.classification)) {
        markApplePayDomainFailure();
      }

      if (mappedError === null) {
        handleCancel();
        return;
      }

      const messageKey = APPCHARGE_ERROR_TYPE_TO_KEY[mappedError];
      setError?.(t(`appchargeErrors.${messageKey}`));

      notifyPaymentIntentFailed(checkoutData.data!, paymentMethod);
      isPaymentInProgress.current = false;
      isPaySessionStartingRef.current = false;
    },
    [setError, t, checkoutData.data, handleCancel, getDiagnostics]
  );

  return { handleError, handleCancel };
};
